import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  InputNumber,
  Space,
  Tag,
  Spin,
  Modal,
  message,
  Typography,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { theme } from '../../theme';

const { Text } = Typography;

interface FardoData {
  id: number;
  nro_fardo: number;
  kilos_fardo: number;
  kilos_tara: number;
  fecha_hora: string | null;
}

interface ProduccionData {
  id: number;
  numero_produccion: number;
  fecha: string | null;
  cliente: { id: number; denominacion: string } | null;
  productor: { id: number; denominacion: string } | null;
  producto: { id: number; descripcion: string } | null;
  fardos: FardoData[];
  kg_fibra: number;
  kilos_semilla: number;
  rinde_semilla: number;
  coeficiente: number;
  coeficiente_no_configurado: boolean;
  es_fibra: boolean;
}

interface FardoDraft {
  nro_fardo: number | null;
  kilos_fardo: number | null;
  kilos_tara: number | null;
  fecha_hora: string;
}

const NEW_ROW_ID = -1;

const EditarProduccion: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { id_empresa, id_sucursal, id_produccion } = useParams<{
    id_empresa: string;
    id_sucursal: string;
    id_produccion: string;
  }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<ProduccionData | null>(null);

  const [fecha, setFecha] = useState<string>('');
  const [kilosSemillaManual, setKilosSemillaManual] = useState<number | null>(null);

  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [draft, setDraft] = useState<FardoDraft>({ nro_fardo: null, kilos_fardo: null, kilos_tara: null, fecha_hora: '' });
  const [draftError, setDraftError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchRegistro = async () => {
    if (!id_empresa || !id_sucursal || !id_produccion) return;
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_PRODUCCION_URL}/registro_produccion/${id_empresa}/${id_sucursal}/${id_produccion}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await response.json();
      if (result.status === 'success') {
        setData(result.data);
        setFecha(result.data.fecha || '');
        setKilosSemillaManual(result.data.kilos_semilla ?? null);
      } else {
        message.error(result.message || 'No se pudo cargar la producción');
      }
    } catch (error) {
      console.error('Error al cargar producción:', error);
      message.error('Error al cargar la producción');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_empresa, id_sucursal, id_produccion]);

  const aplicarCalculos = (calculos: Partial<ProduccionData>) => {
    setData(prev => (prev ? { ...prev, ...calculos } : prev));
    if (calculos.kilos_semilla !== undefined) {
      setKilosSemillaManual(calculos.kilos_semilla);
    }
  };

  const handleStartEdit = (fardo: FardoData) => {
    setEditingRowId(fardo.id);
    setDraft({
      nro_fardo: fardo.nro_fardo,
      kilos_fardo: fardo.kilos_fardo,
      kilos_tara: fardo.kilos_tara,
      fecha_hora: fardo.fecha_hora || '',
    });
    setDraftError(null);
  };

  const handleStartNew = async () => {
    if (!data?.producto?.id || !data?.cliente?.id || !id_empresa || !id_sucursal) {
      message.error('La producción no tiene producto o cliente asignado');
      return;
    }
    let sugerido = 1;
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_PRODUCCION_URL}/ultimo_fardo_desmotado/${id_empresa}/${id_sucursal}/${data.producto.id}/${data.cliente.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await response.json();
      if (result.status === 'success' && typeof result.ultimo_fardo === 'number') {
        sugerido = result.ultimo_fardo + 1;
      }
    } catch (error) {
      console.error('Error al sugerir número de fardo:', error);
    }

    setEditingRowId(NEW_ROW_ID);
    setDraft({
      nro_fardo: sugerido,
      kilos_fardo: null,
      kilos_tara: null,
      fecha_hora: dayjs().format('YYYY-MM-DD HH:mm:ss'),
    });
    setDraftError(null);
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setDraft({ nro_fardo: null, kilos_fardo: null, kilos_tara: null, fecha_hora: '' });
    setDraftError(null);
  };

  const handleApply = async () => {
    if (!id_empresa || !id_sucursal || editingRowId === null) return;
    if (!draft.nro_fardo || draft.kilos_fardo === null || draft.kilos_tara === null || !draft.fecha_hora) {
      setDraftError('Complete todos los campos del fardo');
      return;
    }

    setApplying(true);
    setDraftError(null);
    try {
      const token = await getAccessTokenSilently();
      const isNew = editingRowId === NEW_ROW_ID;
      const url = isNew
        ? `${process.env.REACT_APP_PRODUCCION_URL}/agregar_fardo_produccion/${id_empresa}/${id_sucursal}/${id_produccion}`
        : `${process.env.REACT_APP_PRODUCCION_URL}/modificar_fardo/${id_empresa}/${id_sucursal}/${editingRowId}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nro_fardo: draft.nro_fardo,
          kilos_fardo: draft.kilos_fardo,
          kilos_tara: draft.kilos_tara,
          fecha_hora: draft.fecha_hora,
        }),
      });
      const result = await response.json();

      if (result.status !== 'success') {
        setDraftError(result.message || 'No se pudo guardar el fardo');
        return;
      }

      if (isNew) {
        setData(prev =>
          prev
            ? {
                ...prev,
                fardos: [
                  ...prev.fardos,
                  {
                    id: result.fardo_id,
                    nro_fardo: draft.nro_fardo!,
                    kilos_fardo: draft.kilos_fardo!,
                    kilos_tara: draft.kilos_tara!,
                    fecha_hora: draft.fecha_hora,
                  },
                ],
              }
            : prev
        );
        message.success('Fardo agregado correctamente');
      } else {
        setData(prev =>
          prev
            ? {
                ...prev,
                fardos: prev.fardos.map(f =>
                  f.id === editingRowId
                    ? { ...f, nro_fardo: draft.nro_fardo!, kilos_fardo: draft.kilos_fardo!, kilos_tara: draft.kilos_tara!, fecha_hora: draft.fecha_hora }
                    : f
                ),
              }
            : prev
        );
        message.success('Fardo actualizado correctamente');
      }

      aplicarCalculos({
        kg_fibra: result.kg_fibra,
        kilos_semilla: result.kilos_semilla,
        rinde_semilla: result.rinde_semilla,
        coeficiente: result.coeficiente,
        coeficiente_no_configurado: result.coeficiente_no_configurado,
        es_fibra: result.es_fibra,
      });

      handleCancelEdit();
    } catch (error) {
      console.error('Error al aplicar fardo:', error);
      setDraftError('Error al guardar el fardo');
    } finally {
      setApplying(false);
    }
  };

  const handleDelete = (fardo: FardoData) => {
    Modal.confirm({
      title: `Eliminar fardo N° ${fardo.nro_fardo}`,
      icon: <ExclamationCircleOutlined style={{ color: theme.colors.error }} />,
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        if (!id_empresa || !id_sucursal) return;
        setDeletingId(fardo.id);
        try {
          const token = await getAccessTokenSilently();
          const response = await fetch(
            `${process.env.REACT_APP_PRODUCCION_URL}/eliminar_fardo/${id_empresa}/${id_sucursal}/${fardo.id}`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const result = await response.json();
          if (result.status !== 'success') {
            message.error(result.message || 'No se pudo eliminar el fardo');
            return;
          }
          setData(prev => (prev ? { ...prev, fardos: prev.fardos.filter(f => f.id !== fardo.id) } : prev));
          aplicarCalculos({
            kg_fibra: result.kg_fibra,
            kilos_semilla: result.kilos_semilla,
            rinde_semilla: result.rinde_semilla,
            coeficiente: result.coeficiente,
            coeficiente_no_configurado: result.coeficiente_no_configurado,
            es_fibra: result.es_fibra,
          });
          message.success('Fardo eliminado correctamente');
        } catch (error) {
          console.error('Error al eliminar fardo:', error);
          message.error('Error al eliminar el fardo');
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const handleGuardarCabecera = async () => {
    if (!id_empresa || !id_sucursal || !id_produccion) return;
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const body: Record<string, any> = { fecha };
      if (data?.coeficiente_no_configurado && kilosSemillaManual !== null) {
        body.kilos_semilla = kilosSemillaManual;
      }
      const response = await fetch(
        `${process.env.REACT_APP_PRODUCCION_URL}/actualizar_produccion/${id_empresa}/${id_sucursal}/${id_produccion}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );
      const result = await response.json();
      if (result.status !== 'success') {
        message.error(result.message || 'No se pudo guardar la producción');
        return;
      }
      aplicarCalculos({
        kg_fibra: result.kg_fibra,
        kilos_semilla: result.kilos_semilla,
        rinde_semilla: result.rinde_semilla,
        coeficiente: result.coeficiente,
        coeficiente_no_configurado: result.coeficiente_no_configurado,
        es_fibra: result.es_fibra,
      });
      message.success('Producción actualizada correctamente');
    } catch (error) {
      console.error('Error al guardar producción:', error);
      message.error('Error al guardar la producción');
    } finally {
      setSaving(false);
    }
  };

  const handleAnular = () => {
    if (!data) return;
    Modal.confirm({
      title: 'Confirmar anulación',
      icon: <ExclamationCircleOutlined style={{ color: theme.colors.error }} />,
      content: (
        <div>
          <p>
            Se va a anular la <strong>Producción Nro. {data.numero_produccion}</strong>
            {data.cliente ? ` (${data.cliente.denominacion})` : ''}.
          </p>
          <p>Esto liberará los tickets de balanza y los fardos asociados.</p>
          <p style={{ color: theme.colors.error }}>⚠️ Esta acción no se puede deshacer desde la pantalla.</p>
        </div>
      ),
      okText: 'Sí, anular producción',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        if (!id_empresa || !id_sucursal || !id_produccion) return;
        try {
          const token = await getAccessTokenSilently();
          const response = await fetch(
            `${process.env.REACT_APP_PRODUCCION_URL}/anular_produccion/${id_empresa}/${id_sucursal}/${id_produccion}`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const result = await response.json();
          if (result.status !== 'success') {
            message.error(result.message || 'No se pudo anular la producción');
            return;
          }
          message.success('Producción anulada correctamente');
          navigate('/ListadoProduccion');
        } catch (error) {
          console.error('Error al anular producción:', error);
          message.error('Error al anular la producción');
        }
      },
    });
  };

  const tableData = useMemo(() => {
    const rows = data?.fardos.map(f => ({ ...f })) || [];
    if (editingRowId === NEW_ROW_ID) {
      rows.push({
        id: NEW_ROW_ID,
        nro_fardo: draft.nro_fardo ?? 0,
        kilos_fardo: draft.kilos_fardo ?? 0,
        kilos_tara: draft.kilos_tara ?? 0,
        fecha_hora: draft.fecha_hora,
      });
    }
    return rows;
  }, [data, editingRowId, draft]);

  const columns: ColumnsType<FardoData> = [
    {
      title: 'Nro. Fardo',
      dataIndex: 'nro_fardo',
      key: 'nro_fardo',
      render: (_, record) => {
        if (record.id === editingRowId) {
          return (
            <div>
              <InputNumber
                value={draft.nro_fardo ?? undefined}
                onChange={value => setDraft(prev => ({ ...prev, nro_fardo: value as number }))}
                size="small"
                status={draftError ? 'error' : undefined}
                style={{ width: 90 }}
              />
              {editingRowId === NEW_ROW_ID && !draftError && (
                <div style={{ fontSize: 10.5, color: theme.colors.secondary, fontWeight: 700, marginTop: 3 }}>● sugerido</div>
              )}
              {draftError && (
                <div style={{ fontSize: 11.5, color: theme.colors.error, marginTop: 3 }}>⚠️ {draftError}</div>
              )}
            </div>
          );
        }
        return record.nro_fardo;
      },
    },
    {
      title: 'Kg. Fardo',
      dataIndex: 'kilos_fardo',
      key: 'kilos_fardo',
      align: 'right' as const,
      render: (value, record) => {
        if (record.id === editingRowId) {
          return (
            <InputNumber
              value={draft.kilos_fardo ?? undefined}
              onChange={value => setDraft(prev => ({ ...prev, kilos_fardo: value as number }))}
              size="small"
              style={{ width: 90 }}
            />
          );
        }
        return (value || 0).toFixed(2);
      },
    },
    {
      title: 'Kg. Tara',
      dataIndex: 'kilos_tara',
      key: 'kilos_tara',
      align: 'right' as const,
      render: (value, record) => {
        if (record.id === editingRowId) {
          return (
            <InputNumber
              value={draft.kilos_tara ?? undefined}
              onChange={value => setDraft(prev => ({ ...prev, kilos_tara: value as number }))}
              size="small"
              style={{ width: 80 }}
            />
          );
        }
        return (value || 0).toFixed(2);
      },
    },
    {
      title: 'Fecha/Hora',
      dataIndex: 'fecha_hora',
      key: 'fecha_hora',
      render: (value, record) => {
        if (record.id === editingRowId) {
          return (
            <Input
              value={draft.fecha_hora}
              onChange={e => setDraft(prev => ({ ...prev, fecha_hora: e.target.value }))}
              size="small"
              style={{ width: 150 }}
              placeholder="YYYY-MM-DD HH:mm:ss"
            />
          );
        }
        return value || '-';
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center' as const,
      render: (_, record) => {
        if (record.id === editingRowId) {
          return (
            <Space size="small">
              <Button
                size="small"
                icon={<CheckOutlined />}
                loading={applying}
                onClick={handleApply}
                style={{ backgroundColor: theme.colors.secondary, borderColor: theme.colors.secondary, color: '#fff' }}
                title="Aplicar"
              />
              <Button size="small" icon={<CloseOutlined />} onClick={handleCancelEdit} title="Cancelar" disabled={applying} />
            </Space>
          );
        }
        if (editingRowId !== null) {
          // Mientras se edita otra fila, no mostrar acciones para evitar ediciones simultáneas
          return null;
        }
        return (
          <Space size="small">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleStartEdit(record)}
              style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary, color: '#fff' }}
              title="Editar"
            />
            <Button
              size="small"
              danger
              type="primary"
              icon={<DeleteOutlined />}
              loading={deletingId === record.id}
              onClick={() => handleDelete(record)}
              title="Eliminar"
            />
          </Space>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" tip="Cargando producción..." />
        </div>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Text type="danger">No se encontró la producción solicitada.</Text>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: 0 }}>
      <Card
        style={{ marginBottom: 16, borderRadius: theme.styles.borderRadius, boxShadow: theme.styles.boxShadow }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
          },
        }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              Producción Nro. {data.numero_produccion}
              {data.fecha ? ` · ${dayjs(data.fecha).format('DD/MM/YYYY')}` : ''}
            </span>
            <Button danger type="primary" icon={<DeleteOutlined />} size="small" onClick={handleAnular}>
              Anular producción
            </Button>
          </div>
        }
      >
        <style>{`
          .editar-produccion-grid {
            display: grid;
            gap: 16px;
          }
          .editar-produccion-grid.columns-4 {
            grid-template-columns: repeat(4, 1fr);
          }
          .editar-produccion-grid.columns-2 {
            grid-template-columns: repeat(2, 1fr);
          }
          @media (max-width: 1200px) {
            .editar-produccion-grid.columns-4 {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (max-width: 768px) {
            .editar-produccion-grid.columns-4,
            .editar-produccion-grid.columns-2 {
              grid-template-columns: 1fr;
            }
          }
        `}</style>

        <Space style={{ marginBottom: 12 }}>
          <Button icon={<ArrowLeftOutlined />} size="small" onClick={() => navigate('/ListadoProduccion')}>
            Volver al listado
          </Button>
        </Space>

        {data.es_fibra && (
          <Tag color={data.coeficiente_no_configurado ? 'orange' : 'blue'} style={{ marginBottom: 12 }}>
            {data.coeficiente_no_configurado
              ? '⚠️ Coeficiente Semilla no configurado'
              : `ℹ️ Coeficiente Semilla vigente (SEM): ${data.coeficiente.toFixed(2)}`}
          </Tag>
        )}

        <div className="editar-produccion-grid columns-4" style={{ marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, color: theme.colors.textPrimary, fontSize: 12, marginBottom: 4 }}>
              Fecha
            </label>
            <Input
              type="date"
              value={fecha}
              onChange={e => setFecha(e.target.value)}
              size="small"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, color: theme.colors.textPrimary, fontSize: 12, marginBottom: 4 }}>
              Cliente
            </label>
            <Input value={data.cliente?.denominacion || '-'} size="small" readOnly disabled />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, color: theme.colors.textPrimary, fontSize: 12, marginBottom: 4 }}>
              Productor
            </label>
            <Input value={data.productor?.denominacion || '-'} size="small" readOnly disabled />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, color: theme.colors.textPrimary, fontSize: 12, marginBottom: 4 }}>
              Producto
            </label>
            <Input value={data.producto?.descripcion || '-'} size="small" readOnly disabled />
          </div>
        </div>

        <div className="editar-produccion-grid columns-2" style={{ marginBottom: 8 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, color: theme.colors.textPrimary, fontSize: 12, marginBottom: 4 }}>
              Kg. Fibra{' '}
              <Tag color="green" style={{ marginLeft: 4 }}>
                ● en vivo
              </Tag>
            </label>
            <Input value={data.kg_fibra.toFixed(2)} size="small" readOnly disabled />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, color: theme.colors.textPrimary, fontSize: 12, marginBottom: 4 }}>
              Kg. Semilla{' '}
              {data.coeficiente_no_configurado ? (
                <Tag color="orange" style={{ marginLeft: 4 }}>
                  editable
                </Tag>
              ) : (
                <Tag color="green" style={{ marginLeft: 4 }}>
                  ● en vivo
                </Tag>
              )}
            </label>
            {data.coeficiente_no_configurado ? (
              <InputNumber
                value={kilosSemillaManual ?? undefined}
                onChange={value => setKilosSemillaManual(value as number)}
                size="small"
                style={{ width: '100%' }}
              />
            ) : (
              <Input value={data.kilos_semilla.toFixed(2)} size="small" readOnly disabled />
            )}
          </div>
        </div>

        <div style={{ textAlign: 'right', marginTop: 8 }}>
          <Button
            type="primary"
            loading={saving}
            onClick={handleGuardarCabecera}
            style={{ backgroundColor: theme.colors.secondary, borderColor: theme.colors.secondary, minWidth: 100, height: 32 }}
          >
            Guardar
          </Button>
        </div>
      </Card>

      <Card
        title={`Fardos de la producción (${data.fardos.length})`}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            onClick={handleStartNew}
            disabled={editingRowId !== null}
            style={{ backgroundColor: theme.colors.secondary, borderColor: theme.colors.secondary }}
          >
            Agregar fardo
          </Button>
        }
        style={{ borderRadius: theme.styles.borderRadius, boxShadow: theme.styles.boxShadow }}
      >
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default EditarProduccion;
